// SPECS: __tests__/nav-group-filtering.test.ts
'use client';

import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { Circle, Zap } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useAuth } from '@/lib/useAuth';
import { useUI } from '@/components/UIProvider';
import { ICON_MAP } from '@/components/navigation/NavIconMap';
import { filterNavGroups, getNavPreset, isNavItemActive, NAV_PRESETS } from '@/lib/platform/navigation';
import type { NavGroup } from '@/lib/platform/navigation';

export function Navigation() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { user, authEnabled, loading } = useAuth();
  const { isMobileSimulatorEnabled, isMobileMenuOpen, setMobileMenuOpen } = useUI();
  const navRef = useRef<HTMLElement>(null);

  const preset = user ? getNavPreset(user.role) : 'owner';
  const presetGroups: NavGroup[] = !authEnabled ? NAV_PRESETS.owner : NAV_PRESETS[preset];
  const navGroups: NavGroup[] = filterNavGroups(presetGroups, {
    authEnabled,
    loading,
    role: user?.role ?? null,
  });

  // Close menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname, setMobileMenuOpen]);

  const visibleItems = navGroups.flatMap((group) => group.items);

  // Handle outside click for mobile drawer
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isMobileMenuOpen &&
        navRef.current &&
        !navRef.current.contains(event.target as Node)
      ) {
        setMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen, setMobileMenuOpen]);

  const sidebarContent = (
    <div className="flex flex-col w-64 bg-zinc-950 border-r border-white/5 h-full z-50 overflow-y-auto overflow-x-hidden">
      {/* Logo */}
      <div className="p-6 sticky top-0 bg-zinc-950 z-10 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-brand-gold flex flex-shrink-0 items-center justify-center shadow-[0_0_15px_rgba(251,191,36,0.2)]">
          <Zap className="w-5 h-5 text-black" />
        </div>
        <span className="text-white font-bold tracking-wide">Mission Control</span>
      </div>

      {/* Nav Groups */}
      <div className="flex-1 px-4 pb-8 space-y-8">
        {navGroups.map((group) => (
          <div key={group.group}>
            <h3 className="mb-3 px-2 text-xs font-bold tracking-wider text-zinc-500 uppercase">
              {group.group}
            </h3>
            <div className="space-y-1">
              {group.items.map((item) => {
                // Tab-named entries (e.g. /customers-hub?tab=estimates) light up by the
                // tab value in the address alone, and a tab entry suppresses its plain
                // parent entry on the same page, so at most one entry is lit at once.
                const isActive = isNavItemActive(item, pathname, searchParams, visibleItems);
                const Icon = ICON_MAP[item.icon] ?? Circle;
                return (
                  <Link
                    key={item.name}
                    href={item.href}
                    className={`
                      flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                      ${isActive
                        ? 'bg-brand-gold/10 text-white shadow-[inset_0_0_0_1px_rgba(251,191,36,0.2)]'
                        : 'text-zinc-400 hover:bg-white/5 hover:text-white'
                      }
                    `}
                  >
                    <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-brand-gold' : 'text-zinc-500'}`} />
                    <span className="truncate">{item.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="p-4 mt-auto border-t border-white/5 bg-zinc-950">
        <div className="text-[10px] font-mono text-zinc-600 uppercase tracking-widest text-center">
          Unified Command v2.1
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop & Mobile Navigation Wrapper */}
      <nav 
        ref={navRef}
        aria-label="Main navigation" 
        data-no-print 
        className={`
          fixed inset-y-0 left-0 z-50 flex h-full w-64 flex-shrink-0 transition-transform duration-300
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          ${!isMobileSimulatorEnabled ? 'md:relative md:translate-x-0' : ''}
          ${isMobileSimulatorEnabled ? 'shadow-2xl' : ''}
        `}
      >
        {sidebarContent}
      </nav>

      {/* Mobile backdrop */}
      {isMobileMenuOpen && (
        <div 
          data-no-print 
          className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${!isMobileSimulatorEnabled ? 'md:hidden' : ''}`}
          onClick={() => setMobileMenuOpen(false)} 
        />
      )}
    </>
  );
}
